(() => {
  'use strict';
  if (window.LRLV2Shell?.version === 1) { window.LRLV2Shell.init(); return; }
  const instances = new Map();
  const storageKey = 'lrl.motorcycle.discovery.v2';
  const create = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };
  const safeURL = (value, origin = location.origin) => {
    try {
      const url = new URL(value, origin);
      return url.origin === location.origin || url.hostname === 'lrlmotors.com' ? url : null;
    } catch { return null; }
  };
  class Shell {
    constructor(root) {
      this.root = root;
      this.config = JSON.parse(root.querySelector('[data-lrl-v2-shell-config]').textContent);
      this.abort = new AbortController();
      this.timers = new Set();
      this.selected = null;
      this.vehicle = null;
      this.make = '';
      this.mode = 'make';
      this.requestId = 0;
      this.activeOption = -1;
      this.returnFocus = null;
      this.query = root.querySelector('[data-lrl-v2-bike-query]');
      this.results = root.querySelector('[data-lrl-v2-bike-results]');
      this.selection = root.querySelector('[data-lrl-v2-bike-selection]');
      this.submit = root.querySelector('[data-lrl-v2-bike-submit]');
      this.dialogs = [...root.querySelectorAll('[data-lrl-v2-dialog]')];
      this.bindDialogs();
      this.bindBike();
      this.bindSearch();
      this.bindOffers();
      this.bindCart();
      this.bindRiderTools();
      this.on(window, 'storage', e => { if (e.key === storageKey) this.loadVehicle().then(() => this.restoreBike()).catch(() => {}); });
      let hasSaved = false;
      try { hasSaved = !!localStorage.getItem(storageKey); } catch {}
      if (hasSaved) this.loadVehicle().catch(() => {});
    }
    on(target, name, callback, options = {}) {
      target.addEventListener(name, callback, {...options, signal: this.abort.signal});
    }
    later(callback, delay) {
      const id = setTimeout(() => { this.timers.delete(id); if (!this.abort.signal.aborted) callback(); }, delay);
      this.timers.add(id);
      return id;
    }
    text(key, values = {}) {
      let result = this.config.strings[key] || '';
      for (const [name, value] of Object.entries(values)) result = result.split(`__${name}__`).join(String(value));
      return result;
    }
    emitBike() {
      document.dispatchEvent(new CustomEvent('lrl:v2:bike-change', {detail:{model:this.getBike()}}));
    }
    getBike() { return this.selected ? {...this.selected, makeLabel:this.maker(this.selected.make)?.label || ''} : null; }
    maker(id) { return this.vehicle?.makes.find(make => make.value === id); }
    bindDialogs() {
      this.on(this.root, 'click', e => {
        const close = e.target.closest('[data-lrl-v2-close]');
        if (close) this.closeDialogs();
      });
      this.dialogs.forEach(dialog => {
        this.on(dialog, 'click', e => {
          if (e.target !== dialog) return;
          const rect = dialog.getBoundingClientRect();
          if (e.clientX < rect.left || e.clientX > rect.right || e.clientY < rect.top || e.clientY > rect.bottom) this.closeDialogs();
        });
        this.on(dialog, 'cancel', e => { e.preventDefault(); this.closeDialogs(); });
        this.on(dialog, 'close', () => {
          if (!this.dialogs.some(item => item.open) && this.returnFocus?.isConnected) this.returnFocus.focus({preventScroll:true});
        });
      });
    }
    closeDialogs(restore = true) {
      const oldFocus = this.returnFocus;
      this.dialogs.forEach(dialog => { if (dialog.open) dialog.close(); });
      if (restore && oldFocus?.isConnected) oldFocus.focus({preventScroll:true});
    }
    openDialog(name, trigger, stage = 'summary') {
      const dialog = this.dialogs.find(item => item.dataset.lrlV2Dialog === name);
      if (!dialog) return;
      const insideDialog = trigger?.closest('[data-lrl-v2-dialog]');
      if (!insideDialog) this.returnFocus = trigger || document.activeElement;
      this.closeSearch();
      this.closeDialogs(false);
      if (typeof dialog.showModal !== 'function') {
        if (name === 'bike') location.assign(this.config.searchUrl);
        return;
      }
      dialog.showModal();
      if (name === 'bike') {
        this.make = this.selected?.make || this.make;
        this.mode = stage === 'make' ? 'make' : stage === 'model' && this.make ? 'model' : this.selected ? 'selected' : 'make';
        this.query.value = '';
        this.renderBike();
        this.loadVehicle().then(() => {
          if (!dialog.open) return;
          if (stage === 'summary' && this.selected) this.mode = 'selected';
          this.renderBike();
        }).catch(() => { if (dialog.open) this.renderBikeError(); });
      }
      dialog.querySelector('[data-lrl-v2-close]')?.focus({preventScroll:true});
    }
    async loadVehicle() {
      if (this.vehicle) return this.vehicle;
      if (this.vehiclePromise) return this.vehiclePromise;
      this.vehiclePromise = fetch(this.config.vehicleUrl, {signal:this.abort.signal}).then(response => {
        if (!response.ok) throw new Error('vehicle');
        return response.json();
      }).then(data => {
        if (!Array.isArray(data.makes) || !Array.isArray(data.models)) throw new Error('vehicle');
        this.vehicle = {
          makes:data.makes.filter(make => typeof make.value === 'string' && typeof make.label === 'string'),
          models:data.models.filter(model => {
            const url = safeURL(model.url);
            return typeof model.make === 'string' && typeof model.label === 'string' && url?.hostname === 'lrlmotors.com' && url.pathname === '/pages/search-results-page' && url.searchParams.has('q');
          })
        };
        this.restoreBike();
        return this.vehicle;
      }).catch(error => { this.vehiclePromise = null; throw error; });
      return this.vehiclePromise;
    }
    restoreBike() {
      let saved;
      try { saved = JSON.parse(localStorage.getItem(storageKey) || 'null'); } catch {}
      this.selected = saved && this.vehicle.models.find(model => model.make === saved.make && model.label === saved.label && model.url === saved.url) || null;
      if (this.selected) this.make = this.selected.make;
      this.emitBike();
    }
    saveBike() {
      try {
        if (this.selected) localStorage.setItem(storageKey, JSON.stringify(this.selected));
        else localStorage.removeItem(storageKey);
      } catch {}
      this.emitBike();
    }
    clearBike() {
      this.selected = null; this.make = ''; this.mode = 'make'; this.query.value = '';
      this.saveBike();
      if (this.dialogs.some(dialog => dialog.open && dialog.dataset.lrlV2Dialog === 'bike')) { this.renderBike(); this.query.focus(); }
    }
    bikeLink(model) {
      const url = safeURL(model.url);
      return url ? url.pathname + url.search : this.config.searchUrl;
    }
    bindBike() {
      this.on(this.query, 'input', () => this.renderBike());
      this.on(this.query, 'keydown', e => {
        if (e.key === 'ArrowDown') { const first = this.results.querySelector('button'); if (first) { e.preventDefault(); first.focus(); } }
      });
      this.on(this.root, 'click', e => {
        const step = e.target.closest('[data-lrl-v2-step]');
        if (step && !step.disabled) { this.mode = step.dataset.lrlV2Step; this.query.value = ''; this.renderBike(); this.query.focus(); }
        const make = e.target.closest('[data-lrl-v2-make]');
        if (make) { this.make = make.dataset.lrlV2Make; this.mode = 'model'; this.query.value = ''; this.renderBike(); this.query.focus(); }
        const model = e.target.closest('[data-lrl-v2-model]');
        if (model) {
          this.selected = this.vehicle.models[Number(model.dataset.lrlV2Model)];
          this.make = this.selected.make; this.mode = 'selected'; this.query.value = ''; this.saveBike(); this.renderBike(); this.submit.focus();
        }
        if (e.target.closest('[data-lrl-v2-change-bike]')) { this.mode = 'make'; this.query.value = ''; this.renderBike(); this.query.focus(); }
      });
      this.on(this.submit, 'click', () => { if (this.selected) location.assign(this.bikeLink(this.selected)); });
    }
    renderBikeError() {
      this.results.replaceChildren(create('p', 'lrl-v2-shell-bike-message', this.text('bikeUnavailable')));
      this.root.querySelector('[data-lrl-v2-bike-fallback]').hidden = false;
    }
    renderBike() {
      const q = this.query.value.trim().toLowerCase();
      const selected = this.mode === 'selected' && !!this.selected && !q;
      const make = this.maker(this.make);
      const makeStep = this.root.querySelector('[data-lrl-v2-step=make]');
      const modelStep = this.root.querySelector('[data-lrl-v2-step=model]');
      makeStep.querySelector('b').textContent = make?.label || this.text('bikeMake');
      makeStep.setAttribute('aria-current', this.mode === 'make' ? 'step' : 'false');
      modelStep.querySelector('b').textContent = this.text(selected ? 'bikeSelectedModel' : 'bikeModel');
      modelStep.disabled = !this.make;
      modelStep.setAttribute('aria-current', this.mode !== 'make' ? 'step' : 'false');
      this.selection.hidden = !selected; this.results.hidden = selected;
      this.query.parentElement.hidden = selected;
      this.submit.disabled = !selected;
      this.submit.textContent = this.text(selected ? 'bikeExploreParts' : this.mode === 'make' ? 'bikeChooseMake' : 'bikeChooseModel');
      this.query.placeholder = this.mode === 'model' && make ? this.text('bikeModelPlaceholder', {make:make.label}) : this.text('bikePlaceholder');
      this.root.querySelector('[data-lrl-v2-bike-fallback]').hidden = true;
      if (!this.vehicle) { this.results.replaceChildren(create('p', 'lrl-v2-shell-bike-message', this.text('bikeLoading'))); return; }
      if (selected) {
        const box = create('div', 'lrl-v2-shell-bike-selection');
        box.append(this.root.querySelector('[data-lrl-v2-bike-icon]').content.cloneNode(true));
        const copy = create('div', 'lrl-v2-shell-selected-copy');
        copy.append(create('small', '', this.maker(this.selected.make)?.label || ''), create('strong', '', this.selected.label), create('small', '', this.text('bikeSelected')));
        const actions = create('div', 'lrl-v2-shell-selected-actions');
        const change = create('button', '', this.text('bikeChange')); change.type = 'button'; change.dataset.lrlV2ChangeBike = '';
        const clear = create('button', '', this.text('bikeClear')); clear.type = 'button'; clear.dataset.lrlV2ClearBike = '';
        actions.append(change, clear); box.append(copy, actions); this.selection.replaceChildren(box); return;
      }
      const grid = create('div', this.mode === 'make' && !q ? 'lrl-v2-shell-make-grid' : 'lrl-v2-shell-model-grid');
      grid.setAttribute('role', 'group');
      grid.setAttribute('aria-label', this.text(this.mode === 'make' && !q ? 'bikeMakes' : 'bikeModels'));
      if (this.mode === 'make' && !q) {
        this.vehicle.makes.forEach(item => {
          const button = create('button', 'lrl-v2-shell-make'); button.type = 'button'; button.dataset.lrlV2Make = item.value; button.setAttribute('aria-pressed', String(this.make === item.value));
          const bottom = create('small', '', this.text('bikeExploreModels')); const mark = create('span', '', this.make === item.value ? '✓' : '›'); mark.setAttribute('aria-hidden','true'); bottom.append(mark);
          button.append(create('strong', '', item.label), bottom); grid.append(button);
        });
      } else {
        const terms = q.split(/\s+/).filter(Boolean);
        this.vehicle.models.forEach((item, index) => {
          if ((this.mode !== 'make' && this.make && item.make !== this.make) || !terms.every(term => `${item.label} ${this.maker(item.make)?.label || ''}`.toLowerCase().includes(term))) return;
          const button = create('button', 'lrl-v2-shell-model'); button.type = 'button'; button.dataset.lrlV2Model = String(index); button.setAttribute('aria-pressed', String(this.selected?.url === item.url && this.selected?.label === item.label));
          button.append(this.root.querySelector('[data-lrl-v2-bike-icon]').content.cloneNode(true));
          const copy = create('span','lrl-v2-shell-model-copy'); copy.append(create('small', '', this.maker(item.make)?.label || ''), create('strong', '', item.label));
          const mark = create('span', '', this.selected?.url === item.url ? '✓' : '›'); mark.setAttribute('aria-hidden','true'); button.append(copy, mark); grid.append(button);
        });
      }
      if (!grid.childElementCount) {
        const empty = create('div', 'lrl-v2-shell-bike-message');
        const link = create('a', 'lrl-v2-shell-text-link', this.text('bikeSearchFor', {query:this.query.value})); link.href = this.searchLink(this.query.value);
        empty.append(create('strong', '', this.text('bikeNoResults')), create('p', '', this.text('bikeTrySearch')), link); this.results.replaceChildren(empty);
      } else this.results.replaceChildren(grid);
    }
    searchLink(query) { const url = new URL(this.config.searchUrl, location.origin); url.searchParams.set('q', query); url.searchParams.set('options[prefix]','last'); return url.pathname + url.search; }
    bindSearch() {
      this.search = this.root.querySelector('[data-lrl-v2-search]');
      this.searchInput = this.search.querySelector('input[name=q]');
      this.suggestions = this.search.querySelector('.lrl-v2-shell-suggestions[role=listbox]');
      this.searchStatus = this.search.querySelector('[data-lrl-v2-search-status]');
      this.searchObserver = new MutationObserver(() => this.deferSearchToApp());
      this.searchObserver.observe(this.searchInput, {attributes:true,attributeFilter:['aria-controls']});
      this.deferSearchToApp();
      this.on(this.searchInput, 'input', () => {
        if (this.deferSearchToApp()) return;
        clearTimeout(this.searchTimer); this.searchAbort?.abort(); this.requestId++;
        const query = this.searchInput.value.trim();
        if (query.length < 2) { this.closeSearch(); return; }
        this.searchTimer = this.later(() => this.predict(query), 220);
      });
      this.on(this.searchInput, 'focus', () => {
        if (this.deferSearchToApp()) return;
        const query = this.searchInput.value.trim();
        if (query.length >= 2) this.predict(query);
      });
      this.on(this.searchInput, 'keydown', e => {
        if (this.deferSearchToApp()) return;
        const options = [...this.suggestions.querySelectorAll('[role=option]')];
        if (e.key === 'Escape') { e.preventDefault(); this.closeSearch(); return; }
        if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && options.length && !this.suggestions.hidden) {
          e.preventDefault(); const change = e.key === 'ArrowDown' ? 1 : -1;
          this.activeOption = (this.activeOption + change + options.length) % options.length;
          options.forEach((option,index) => option.setAttribute('aria-selected', String(index === this.activeOption)));
          const active = options[this.activeOption]; this.searchInput.setAttribute('aria-activedescendant',active.id); active.scrollIntoView({block:'nearest'});
        } else if (e.key === 'Enter' && this.activeOption >= 0 && !this.suggestions.hidden) {
          const option = options[this.activeOption]; if (option) { e.preventDefault(); location.assign(option.href); }
        } else if (e.key === 'Tab') this.closeSearch();
      });
      this.on(document, 'pointerdown', e => { if (!this.search.contains(e.target)) this.closeSearch(); });
      this.on(this.search, 'focusout', e => { if (!this.search.contains(e.relatedTarget)) this.later(() => this.closeSearch(), 0); });
      this.on(this.search.querySelector('form'), 'submit', () => this.closeSearch());
    }
    searchIsAppOwned() {
      // Searchanise advertises ownership on this input, including after lazy initialization.
      return (this.searchInput?.getAttribute('aria-controls') || '').split(/\s+/).includes('snize-instant-search-results');
    }
    deferSearchToApp() {
      if (!this.searchIsAppOwned()) return false;
      clearTimeout(this.searchTimer); this.timers.delete(this.searchTimer);
      this.searchAbort?.abort(); this.requestId++;
      this.closeSearch(); this.suggestions.replaceChildren(); this.suggestions.removeAttribute('aria-busy');
      this.searchStatus.textContent = '';
      return true;
    }
    closeSearch() {
      if (!this.suggestions) return;
      this.suggestions.hidden = true; this.activeOption = -1;
      if (!this.searchIsAppOwned()) { this.searchInput.setAttribute('aria-expanded','false'); this.searchInput.removeAttribute('aria-activedescendant'); }
    }
    showSearch() { this.suggestions.hidden = false; this.searchInput.setAttribute('aria-expanded','true'); }
    addSearchAll(query) {
      const link = create('a', 'lrl-v2-shell-search-option lrl-v2-shell-search-all', this.text('searchAll',{query}));
      link.href = this.searchLink(query); this.makeOption(link); this.suggestions.append(link);
    }
    makeOption(node) {
      node.setAttribute('role','option'); node.setAttribute('aria-selected','false'); node.tabIndex = -1;
      node.id = `${this.suggestions.id}-option-${this.suggestions.querySelectorAll('[role=option]').length}`;
    }
    formatPrice(item) {
      const number = value => {
        if (typeof value !== 'number' && typeof value !== 'string') return null;
        if (typeof value === 'string' && !/^\d+(?:\.\d+)?$/.test(value.trim())) return null;
        const parsed = Number(value);
        return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
      };
      const minimum = number(item.price_min) ?? number(item.price);
      const maximum = number(item.price_max);
      const currency = this.config.currency;
      if (minimum === null || typeof currency !== 'string' || !/^[A-Z]{3}$/.test(currency)) return null;
      try {
        if (typeof Intl.supportedValuesOf === 'function') {
          if (!Intl.supportedValuesOf('currency').includes(currency)) return null;
        } else if (typeof Intl.DisplayNames === 'function') {
          if (!new Intl.DisplayNames(['en'], {type:'currency',fallback:'none'}).of(currency)) return null;
        } else return null;
        const locale = this.config.locale || document.documentElement.lang || undefined;
        const price = new Intl.NumberFormat(locale, {style:'currency',currency,minimumFractionDigits:Number.isInteger(minimum) ? 0 : undefined}).format(minimum);
        return maximum !== null && minimum < maximum ? this.text('searchFromPrice',{price}) : price;
      } catch { return null; }
    }
    async predict(query) {
      if (this.abort.signal.aborted || this.deferSearchToApp()) return;
      this.searchAbort?.abort();
      const controller = new AbortController(); this.searchAbort = controller;
      const requestId = ++this.requestId; this.activeOption = -1; this.searchInput.removeAttribute('aria-activedescendant');
      this.suggestions.replaceChildren(create('p','lrl-v2-shell-search-message',this.text('searchLoading'))); this.suggestions.setAttribute('aria-busy','true'); this.showSearch();
      const base = this.config.predictiveUrl.replace(/\/$/, '').replace(/\.json$/, '');
      const url = new URL(`${base}.json`,location.origin);
      url.searchParams.set('q',query); url.searchParams.set('resources[type]','product,collection'); url.searchParams.set('resources[limit]','6'); url.searchParams.set('resources[limit_scope]','all'); url.searchParams.set('resources[options][unavailable_products]','last');
      const timeout = this.later(() => controller.abort(),7000);
      try {
        const response = await fetch(url, {headers:{Accept:'application/json'},signal:controller.signal,credentials:'same-origin'});
        if (!response.ok) throw new Error('predictive');
        const data = await response.json();
        if (this.deferSearchToApp() || requestId !== this.requestId || this.abort.signal.aborted || !this.root.isConnected || this.searchInput.value.trim() !== query) return;
        const resources = data.resources?.results;
        if (!resources || !Array.isArray(resources.products)) throw new Error('predictive');
        this.suggestions.replaceChildren();
        const list = [...resources.products.map(item => ({...item,resultType:'product'})), ...(resources.collections || []).map(item => ({...item,resultType:'collection'}))].slice(0,6);
        let count = 0;
        list.forEach(item => {
          const destination = safeURL(item.url); if (!destination) return;
          const link = create('a', 'lrl-v2-shell-search-option'); link.href = destination.pathname + destination.search;
          const imageURL = item.featured_image?.url || item.image || item.featured_image;
          if (typeof imageURL === 'string' && /^(https:)?\/\//.test(imageURL)) {
            const image = new URL(imageURL,location.origin); image.searchParams.set('width','108');
            const img = create('img'); img.src = image.href; img.alt = ''; img.width = 54; img.height = 54; img.loading = 'lazy'; link.append(img);
          } else link.classList.add('no-image');
          const copy = create('span','lrl-v2-shell-search-option-copy');
          const type = this.text(item.resultType === 'product' ? 'searchProduct' : 'searchCollection');
          const meta = [type,item.vendor,item.available === false ? this.text('searchUnavailableProduct') : ''].filter(Boolean).join(' · ');
          copy.append(create('strong','',item.title),create('small','',meta));
          const price = item.resultType === 'product' ? this.formatPrice(item) : null;
          if (price !== null) copy.append(create('span','lrl-v2-shell-search-price',price));
          link.append(copy); this.makeOption(link); this.suggestions.append(link); count++;
        });
        if (!count) this.suggestions.append(create('p','lrl-v2-shell-search-message',this.text('searchEmpty')));
        this.addSearchAll(query); this.searchStatus.textContent = count ? this.text('searchResults',{count:count+1}) : this.text('searchEmpty');
      } catch (error) {
        if (this.deferSearchToApp() || requestId !== this.requestId || this.abort.signal.aborted || this.searchInput.value.trim() !== query) return;
        this.suggestions.replaceChildren(create('p','lrl-v2-shell-search-message',this.text('searchUnavailable'))); this.addSearchAll(query); this.searchStatus.textContent = this.text('searchUnavailable');
      } finally {
        clearTimeout(timeout); this.timers.delete(timeout);
        if (requestId === this.requestId) this.suggestions.removeAttribute('aria-busy');
      }
    }
    bindOffers() {
      const region = this.root.querySelector('[data-lrl-v2-offers]');
      const messages = [...region.querySelectorAll('[data-lrl-v2-offer]')];
      const toggle = region.querySelector('[data-lrl-v2-offer-toggle]');
      const pause = region.querySelector('[data-lrl-v2-pause]'); const play = region.querySelector('[data-lrl-v2-play]');
      const motion = matchMedia('(prefers-reduced-motion: reduce)');
      let index = 0, paused = false, hovered = false, focused = false, interactionOverride = false, timer, pageHidden = false;
      const render = () => {
        messages.forEach((message, i) => { message.hidden = i !== index; });
        toggle.disabled = motion.matches;
        toggle.setAttribute('aria-pressed',String(paused));
        toggle.setAttribute('aria-label',this.text(motion.matches ? 'offerReduced' : paused ? 'offerResume' : 'offerPause'));
        pause.hidden = paused || motion.matches; play.hidden = !paused && !motion.matches;
      };
      const schedule = () => {
        clearTimeout(timer); this.timers.delete(timer);
        if (!paused && ((!hovered && !focused) || interactionOverride) && !motion.matches && !document.hidden && !pageHidden) timer = this.later(() => { index = (index+1)%messages.length; render(); schedule(); },5500);
      };
      const advance = delta => {
        index = (index + delta + messages.length)%messages.length; paused = true; interactionOverride = false; render(); schedule();
        region.querySelector('[data-lrl-v2-offer-status]').textContent = this.text('offerStatus',{index:index+1,total:messages.length,message:messages[index].textContent.trim()});
      };
      this.on(toggle,'click',() => { paused = !paused; interactionOverride = !paused && (hovered || focused); render(); schedule(); });
      this.on(region.querySelector('[data-lrl-v2-offer-next]'),'click',() => advance(1));
      this.on(region,'keydown',e => { if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {e.preventDefault(); advance(e.key === 'ArrowRight' ? 1 : -1);} });
      this.on(region,'pointerenter',e => {if(e.pointerType !== 'touch') {hovered=true;interactionOverride=false;schedule();}});
      this.on(region,'pointerleave',() => {hovered=false;interactionOverride=false;schedule();});
      this.on(region,'focusin',() => {focused=true;interactionOverride=false;schedule();});
      this.on(region,'focusout',e => {if(!region.contains(e.relatedTarget)){focused=false;interactionOverride=false;schedule();}});
      this.on(document,'visibilitychange',schedule);
      this.on(window,'pagehide',() => {pageHidden=true;schedule();});
      this.on(window,'pageshow',() => {pageHidden=false;schedule();});
      this.on(motion,'change',() => {render();schedule();});
      render(); schedule();
    }
    bindCart() {
      const count = this.root.querySelector('[data-lrl-v2-cart-count]');
      const syncLabel = () => {
        const value = Number(count.textContent.trim());
        if (Number.isFinite(value)) this.root.querySelector('[data-lrl-v2-cart-link]').setAttribute('aria-label',this.text('cartCount',{count:value}));
      };
      const apply = data => {
        const cart = data?.cart || data?.cartData || data;
        if (typeof cart?.item_count === 'number') { count.textContent = String(cart.item_count); syncLabel(); } else this.refreshCart();
      };
      ['cart:updated','cart:change','cart-update','lrl:cart-updated'].forEach(name => this.on(document,name,event => apply(event.detail)));
      this.cartObserver = new MutationObserver(syncLabel); this.cartObserver.observe(count,{childList:true,characterData:true,subtree:true});
      this.on(window,'pageshow',() => this.refreshCart());
      this.on(window,'focus',() => this.refreshCart());
      this.on(document,'visibilitychange',() => {if(!document.hidden)this.refreshCart();});
      syncLabel();
    }
    async refreshCart() {
      if (this.cartLoading || Date.now() - (this.lastCartRefresh || 0) < 1000) return;
      this.cartLoading = true; this.lastCartRefresh = Date.now();
      try {
        const response = await fetch(`${this.config.cartUrl}.js`,{credentials:'same-origin',headers:{Accept:'application/json'},signal:this.abort.signal});
        if (!response.ok) return;
        const cart = await response.json();
        if (typeof cart.item_count === 'number') this.root.querySelector('[data-lrl-v2-cart-count]').textContent=String(cart.item_count);
      } catch {} finally {this.cartLoading=false;}
    }
    bindRiderTools() {
      const link = this.root.querySelector('[data-lrl-v2-reviews]');
      if (!link) return;
      this.on(link, 'click', event => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const launcher = document.querySelector('.jdgm-revs-tab-btn');
        const panel = document.querySelector('.jdgm-revs-tab__wrapper');
        if (!launcher || !panel) return;
        launcher.click();
        if (!panel.classList.contains('jdgm-show')) return;
        event.preventDefault();
        panel.setAttribute('role', 'dialog');
        panel.setAttribute('aria-modal', 'true');
        panel.setAttribute('aria-label', link.getAttribute('aria-label'));
        let focusedClose = null;
        const prepareClose = () => {
          const close = panel.querySelector('.jdgm-close-ico');
          if (!close) return;
          close.setAttribute('aria-label', this.config.labels.closeReviews);
          close.setAttribute('tabindex', '0');
          if (close !== focusedClose) {
            focusedClose = close;
            this.later(() => { if (panel.classList.contains('jdgm-show')) close.focus({preventScroll:true}); }, 0);
          }
        };
        prepareClose();
        this.reviewObserver?.disconnect();
        this.reviewObserver = new MutationObserver(() => {
          if (!panel.classList.contains('jdgm-show')) {
            this.reviewObserver.disconnect();
            this.later(() => { if (link.isConnected) link.focus({preventScroll:true}); }, 0);
          } else prepareClose();
        });
        this.reviewObserver.observe(panel, {attributes:true, attributeFilter:['class'],childList:true,subtree:true});
      });
      this.on(document, 'keydown', event => {
        const panel = document.querySelector('.jdgm-revs-tab__wrapper.jdgm-show');
        if (!panel) return;
        const close = panel.querySelector('.jdgm-close-ico');
        if (event.key === 'Escape') { event.preventDefault(); event.stopImmediatePropagation(); close?.click(); }
        if ((event.key === 'Enter' || event.key === ' ') && document.activeElement === close) { event.preventDefault(); close.click(); }
        if (event.key === 'Tab') {
          const focusable = [...panel.querySelectorAll('a[href],button,select,input,textarea,[tabindex="0"]')].filter(node => node.getClientRects().length && !node.disabled);
          const first = focusable[0], last = focusable[focusable.length - 1];
          if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
          else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
        }
      }, {capture:true});
    }
    destroy() {
      this.closeDialogs(false); this.abort.abort(); this.searchAbort?.abort(); this.timers.forEach(clearTimeout); this.timers.clear(); this.cartObserver?.disconnect(); this.searchObserver?.disconnect(); this.reviewObserver?.disconnect();
    }
  }
  const init = () => {
    let mounted = false;
    instances.forEach((instance,root) => {if(!root.isConnected){instance.destroy();instances.delete(root);}});
    document.querySelectorAll('[data-lrl-v2-header]').forEach(root => {
      if (instances.has(root)) return;
      try {instances.set(root,new Shell(root)); mounted = true;} catch (error) {console.warn('LRL shell could not initialize',error);}
    });
    if (mounted) document.dispatchEvent(new CustomEvent('lrl:v2:ready'));
  };
  const current = () => [...instances.values()].find(instance => instance.root.isConnected);
  window.LRLV2Shell = {
    version:1,init,
    openBike:(stage='summary') => current()?.openDialog('bike',document.activeElement,stage),
    getBike:() => current()?.getBike() || null,
    clearBike:() => current()?.clearBike(),
    refreshCart:() => current()?.refreshCart()
  };
  document.addEventListener('click',event => {
    const opener = event.target.closest('[data-lrl-v2-open]');
    if (opener && current()) {event.preventDefault(); current().openDialog(opener.dataset.lrlV2Open,opener,opener.dataset.lrlV2Stage || 'summary');}
    if (event.target.closest('[data-lrl-v2-clear-bike]')) {event.preventDefault();current()?.clearBike();}
  });
  document.addEventListener('shopify:section:load',init);
  document.addEventListener('shopify:section:unload',event => {
    instances.forEach((instance,root) => {if(event.target.contains(root)){instance.destroy();instances.delete(root);}});
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',init,{once:true}); else init();
})();
